/**
 * Why a deal died.
 *
 * Daniel 2026-08-05, migration 0186. He picked the list: his own three, plus
 * financing and inspection, plus the seller-side ones so the report can say
 * whose side ended it.
 *
 * ═══ Why this lives in shared-types ════════════════════════════════════
 *
 * Because the API validates against it and the UI renders the picker from it,
 * and a reason list that exists twice is a reason list that will disagree.
 * When those two drift, the failure is quiet and ugly: the picker offers an
 * option the API rejects, or the API accepts a value the report has no label
 * for and it shows up as a raw string in front of a client.
 *
 * ═══ Why the side matters ══════════════════════════════════════════════
 *
 * "Buyer backed out" and "seller changed mind" are the same event from
 * opposite ends, and lumping them loses the only thing worth knowing when
 * Daniel reviews his own listings: whether his side or theirs ended it. So
 * every reason carries `side`.
 *
 * `other` exists because the alternative is somebody picking a wrong-but-close
 * reason to get past the form, which pollutes the count worse than an honest
 * "other" does. It requires a note, enforced in the API.
 */

export type DeadReasonSide = 'buyer' | 'seller' | 'either';

export interface DeadReasonDef {
  key: string;
  /** Sentence case, no em dashes. Rendered directly in the picker. */
  label: string;
  side: DeadReasonSide;
  /** Shown under the label so two similar reasons are not a coin toss. */
  hint: string;
}

export const DEAD_REASONS: DeadReasonDef[] = [
  {
    key: 'buyer_backed_out',
    label: 'Buyer backed out',
    side: 'buyer',
    hint: 'They walked with no specific contingency behind it.',
  },
  {
    key: 'financing_fell_through',
    label: 'Financing fell through',
    side: 'buyer',
    hint: 'Loan denied or the buyer no longer qualified.',
  },
  {
    key: 'did_not_appraise',
    label: 'Did not appraise',
    side: 'either',
    hint: 'Appraisal came in under contract price and the gap was not bridged.',
  },
  {
    key: 'appraisal_seller_would_not_reduce',
    label: 'Low appraisal, seller would not reduce',
    side: 'seller',
    hint: 'It appraised low and the seller held the price. Use this rather than '
      + 'Did not appraise when the seller is the reason it ended.',
  },
  {
    key: 'inspection_findings',
    label: 'Inspection findings',
    side: 'buyer',
    hint: 'The buyer terminated on what the inspection turned up.',
  },
  {
    key: 'repairs_no_agreement',
    label: 'Could not agree on repairs',
    side: 'either',
    hint: 'Both sides wanted the deal. The repair list is what ended it.',
  },
  {
    key: 'seller_changed_mind',
    label: 'Seller changed mind',
    side: 'seller',
    hint: 'The seller decided not to sell.',
  },
  {
    key: 'seller_title_problem',
    label: 'Seller could not deliver clear title',
    side: 'seller',
    hint: 'Liens, heirs, survey or boundary problems the seller could not clear.',
  },
  {
    key: 'other',
    label: 'Other',
    side: 'either',
    hint: 'Anything the list does not cover. A note is required.',
  },
];

export const DEAD_REASON_KEYS: string[] = DEAD_REASONS.map((r) => r.key);

export function isDeadReason(v: unknown): boolean {
  return typeof v === 'string' && DEAD_REASON_KEYS.includes(v);
}

export function deadReasonLabel(key: string | null | undefined): string {
  if (!key) return 'Not recorded';
  return DEAD_REASONS.find((r) => r.key === key)?.label ?? key;
}

/**
 * The copy for a deal that has ended, by transaction side.
 *
 * Daniel 2026-08-05: "we need buyer contracts to show buyer contract closed."
 *
 * The close-out email said "The listing agreement has been cancelled" on every
 * deal, including buyer deals, where there is no listing agreement and never
 * was. Same shape as two other bugs found today: copy that assumed the listing
 * side because the listing side was built first. On a buyer deal the agreement
 * that ended is the buyer's contract.
 *
 * `withdrawn` and `expired` are listing concepts and the close-out endpoint
 * already refuses them on buyer deals, so the buyer column only needs
 * `cancelled`.
 */
export function outcomeMeaning(
  outcome: 'cancelled' | 'withdrawn' | 'expired',
  txType: string | null | undefined,
): string {
  const isBuyerDeal = txType === 'buyer';
  if (isBuyerDeal) {
    return 'The buyer contract has been cancelled. There is no contract in place.';
  }
  switch (outcome) {
    case 'withdrawn':
      return 'The listing is off the market. The agreement is still in place.';
    case 'expired':
      return 'The listing agreement expired. There is no agreement in place.';
    default:
      return 'The listing agreement has been cancelled. There is no agreement in place.';
  }
}

/** Headline for the close-out email and the deal header. */
export function outcomeHeadline(
  outcome: 'cancelled' | 'withdrawn' | 'expired',
  txType: string | null | undefined,
): string {
  if (txType === 'buyer' && outcome === 'cancelled') return 'Buyer contract cancelled';
  return outcome.charAt(0).toUpperCase() + outcome.slice(1);
}
